import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Eyebrow, MaskLines, Reveal, EASE } from "./motion";

const SERVICES = [
  {
    key: "contract",
    title: "Contract Cleaning",
    kicker: "Janitorial · Recurring",
    img: "/img/office.jpg",
    text: "Scheduled janitorial programs for offices, dealerships, community centers and commercial buildings — nightly, weekly or on the rhythm your facility runs on.",
    items: ["Offices, lobbies & common areas", "Restrooms & break rooms", "Trash, recycling & dusting", "Day porter or after-hours crews"],
    span: "lg:col-span-2 lg:row-span-2",
  },
  {
    key: "floor",
    title: "Floor Care",
    kicker: "Restore · Protect",
    img: "/img/hallway.jpg",
    text: "The finish people notice first. We bring tired floors back and keep them that way.",
    items: ["Strip & refinish", "Scrub & recoat", "High-speed burnishing", "Carpet extraction"],
    span: "lg:col-span-2",
  },
  {
    key: "industrial",
    title: "Industrial Cleaning",
    kicker: "Plants · Warehouses",
    img: "/img/warehouse.jpg",
    text: "Warehouses, shops and production spaces kept safe, clean and audit-ready.",
    items: ["Concrete & shop floors", "Dock & bay areas"],
    span: "",
  },
  {
    key: "maintenance",
    title: "General Maintenance",
    kicker: "Upkeep · Repairs",
    img: "/img/tools.jpg",
    text: "The everyday fixes and upkeep that keep a building running.",
    items: ["Routine facility upkeep", "Minor repairs & touch-ups"],
    span: "",
  },
];

const ServiceCard = ({ s, i }) => (
  <motion.article
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, delay: i * 0.08, ease: EASE }}
    data-testid={`service-card-${s.key}`}
    className={`group relative flex min-h-[420px] flex-col justify-end overflow-hidden rounded-[22px] border border-white/10 bg-zinc-900 lg:min-h-0 ${s.span}`}
  >
    <img src={s.img} alt={s.title} className="absolute inset-0 h-full w-full object-cover opacity-60 transition-[transform,opacity] duration-[1.4s] ease-out group-hover:scale-105 group-hover:opacity-75" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/10" />
    <div className="relative flex h-full flex-col justify-between p-6 sm:p-8">
      <div className="flex items-start justify-between">
        <span className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-brand-300">{s.kicker}</span>
        <span className="grid h-10 w-10 place-items-center rounded-full border border-white/20 text-white transition-[background-color,color,transform] duration-500 group-hover:rotate-45 group-hover:bg-brand-400 group-hover:text-[#0A0A0A]">
          <ArrowUpRight className="h-4 w-4" />
        </span>
      </div>
      <div>
        <h3 className={`font-display text-3xl font-extrabold tracking-tight text-white ${s.span ? "sm:text-4xl" : "lg:text-[1.65rem] xl:text-3xl"}`}>{s.title}</h3>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-zinc-300 sm:text-base">{s.text}</p>
        <ul className="mt-5 flex flex-wrap gap-2">
          {s.items.map((it) => (
            <li key={it} className="rounded-full border border-white/15 bg-black/30 px-3 py-1 text-xs text-zinc-200 backdrop-blur-md">{it}</li>
          ))}
        </ul>
      </div>
    </div>
  </motion.article>
);

export const Services = () => (
  <section id="services" data-testid="services-section" className="relative mx-auto max-w-[1440px] px-5 py-24 lg:px-12 lg:py-36">
    <div className="mb-14 grid gap-8 lg:grid-cols-12 lg:items-end">
      <div className="lg:col-span-7">
        <Eyebrow index="01">What we do</Eyebrow>
        <MaskLines className="mt-6 font-display text-4xl font-black uppercase leading-[0.95] tracking-[-0.03em] text-white sm:text-5xl lg:text-6xl" lines={["Three disciplines.", <span className="text-zinc-400">One accountable crew.</span>]} />
      </div>
      <Reveal className="lg:col-span-5" delay={0.2}>
        <p className="text-base leading-relaxed text-zinc-300 md:text-lg">
          Most buildings juggle a cleaning company, a floor contractor and a handyman. We do all three under one contract, so there's one number to call and one team that knows your building.
        </p>
      </Reveal>
    </div>
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-4 lg:grid-rows-[380px_380px]">
      {SERVICES.map((s, i) => <ServiceCard key={s.key} s={s} i={i} />)}
    </div>
  </section>
);
